import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ImageUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiError, uploadImages } from "@/lib/api";
import { DFC_LAYOUTS } from "@/lib/mtg";
import { ManaCost } from "@/components/common/ManaCost";
import { ImageDropzone } from "@/components/common/ImageDropzone";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function ReplaceDialog({ card, face, onClose }) {
  const qc = useQueryClient();
  const [upload, setUpload] = useState(null);
  const [review, setReview] = useState("");
  const [busy, setBusy] = useState(false);
  const f = card.faces[face];
  const pick = async (files) => {
    setBusy(true);
    try {
      const [r] = await uploadImages(files.slice(0, 1), "card_image");
      r.ok ? setUpload(r) : toast.error(`${r.filename}: ${r.error}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/cards/${card.card_id}/faces/${face}/image`, { file_id: upload.file_id, review });
      qc.invalidateQueries({ queryKey: ["cards"] });
      toast.success(review === "flag" ? "Image replaced and flagged for review" : "Image replaced");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-[#2A3447] bg-[#121620] text-slate-200" data-testid="replace-image-dialog">
        <DialogHeader><DialogTitle className="font-heading text-amber-100">Replace {card.faces.length > 1 && DFC_LAYOUTS.includes(card.layout) ? (face ? "back" : "front") + " face" : "card"} image</DialogTitle></DialogHeader>
        <div className="grid gap-5 sm:grid-cols-3">
          <div><p className="eyebrow mb-2">Current</p><img src={assetUrl(f.image_url)} alt="Current" className="aspect-[63/88] w-full rounded border border-[#2A3447] bg-black/40 object-contain" /></div>
          <div>
            <p className="eyebrow mb-2">New</p>
            {upload ? <img src={assetUrl(upload.url)} alt="New" className="aspect-[63/88] w-full rounded border border-amber-600/60 bg-black/40 object-contain" data-testid="replace-image-preview" />
              : <ImageDropzone onFiles={pick} compact testid="replace-image-dropzone" label="Drop the complete card image" hint="PNG, JPG or WebP" />}
            {busy && !upload && <Loader2 className="mx-auto mt-2 h-4 w-4 animate-spin" />}
          </div>
          <div className="space-y-2 text-sm" data-testid="replace-image-details">
            <p className="eyebrow mb-2">Card details on record</p>
            <p className="flex items-center justify-between gap-2 font-heading text-amber-100">{f.name}<ManaCost cost={f.mana_cost} /></p>
            <p className="text-slate-300">{f.type_line}</p>
            <p className="whitespace-pre-line text-xs text-slate-400">{f.oracle_text}</p>
            {(f.power || f.loyalty || f.defense) && <p className="font-mono text-xs text-amber-200">{f.loyalty || f.defense || `${f.power}/${f.toughness}`}</p>}
          </div>
        </div>
        <p className="text-xs text-slate-500">The site does not read text inside images. Compare the new image with the details above yourself. Only the image changes: the card's ID, decks, set, privacy and details stay the same.</p>
        <div className="space-y-2 text-sm">
          <label className="flex items-center gap-2"><input type="radio" name="review" checked={review === "matches"} onChange={() => setReview("matches")} data-testid="replace-review-matches" />The new image matches these details</label>
          <label className="flex items-center gap-2"><input type="radio" name="review" checked={review === "flag"} onChange={() => setReview("flag")} data-testid="replace-review-flag" />Something differs: flag the card so I can review and fix its details</label>
        </div>
        <div className="flex justify-end gap-3">
          <button className="btn-ghost" onClick={onClose} data-testid="replace-image-cancel">Cancel</button>
          <button className="btn-gold" disabled={!upload || !review || busy} onClick={save} data-testid="replace-image-confirm">Replace image</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReplaceFaceImage({ card }) {
  const [face, setFace] = useState(null);
  const printed = DFC_LAYOUTS.includes(card.layout) ? Math.min(2, card.faces.length) : 1;
  return (
    <>
      <div className="flex flex-wrap gap-3">
        {Array.from({ length: printed }, (_, i) => (
          <button key={i} className="btn-outline" onClick={() => setFace(i)} data-testid={`card-replace-image-${i}`}>
            <ImageUp className="h-4 w-4" />Replace {printed > 1 ? (i ? "back" : "front") + " face" : "face"} image
          </button>
        ))}
      </div>
      {face !== null && <ReplaceDialog card={card} face={face} onClose={() => setFace(null)} />}
    </>
  );
}

export function ImageReviewBanner({ card }) {
  const qc = useQueryClient();
  if (!card.image_review) return null;
  const resolve = async () => {
    try {
      await api.post(`/cards/${card.card_id}/image-review/resolve`);
      qc.invalidateQueries({ queryKey: ["cards"] });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-600/50 bg-amber-950/30 p-4 text-sm text-amber-200" data-testid="card-image-review-banner">
      <span>Flagged: the image on face {card.image_review.faces.map((f) => f + 1).join(", ")} may not match the card's details. Review the details and edit them if needed.</span>
      <button className="btn-outline px-3 py-1.5 text-xs" onClick={resolve} data-testid="card-image-review-resolve">Mark as reviewed</button>
    </div>
  );
}
